import React from 'react';

/**
 * Bars in one pattern. The utterance sits inside it with silence at both
 * ends, so the pattern meets itself without a step when it repeats.
 */
const BAR_COUNT = 132;

/** Distance between bars, in drawing units. */
const BAR_PITCH = 11;

/** Half the drawing's height, which is also the tallest a bar can be. */
const PEAK = 34;

const PATTERN_WIDTH = BAR_COUNT * BAR_PITCH;

/**
 * ### Syllable
 *
 * One syllable of the spoken word, as it shows up in a waveform: where it
 * starts, how fast it reaches full amplitude, how loud it gets, and how long
 * it takes to die away. Positions are fractions of the pattern.
 */
type Syllable = {
  /** Where the syllable begins, as a fraction of the pattern */
  start: number;

  /** How long it takes to reach full amplitude */
  attack: number;

  /** How loud it gets, relative to the loudest syllable */
  peak: number;

  /** How long it takes to fall away */
  release: number;
};

/**
 * "디플리", said once. The first syllable opens on a voiced stop and holds;
 * the second is an aspirated ㅍ, which is why it arrives almost instantly
 * and is the loudest of the three; the third begins softly on ㄹ and is the
 * one that trails off. The gaps between them are the closures the mouth
 * makes, and they are as much a part of the shape as the bursts.
 */
const SYLLABLES: ReadonlyArray<Syllable> = [
  { start: 0.1, attack: 0.028, peak: 0.66, release: 0.085 },
  { start: 0.36, attack: 0.006, peak: 1, release: 0.1 },
  { start: 0.63, attack: 0.045, peak: 0.8, release: 0.13 },
];

/**
 * ### amplitude
 *
 * How loud the utterance is at one point in the pattern: the loudest of the
 * syllables sounding there, each rising over its attack and falling away
 * after it.
 *
 * @param position Point in the pattern, from 0 to 1
 * @returns Amplitude at that point, from 0 to 1
 */
function amplitude(position: number): number {
  let level = 0;

  for (const syllable of SYLLABLES) {
    const since = position - syllable.start;
    if (since < 0) continue;

    const shape =
      since < syllable.attack
        ? since / syllable.attack
        : Math.exp(-(since - syllable.attack) / syllable.release);

    level = Math.max(level, syllable.peak * shape);
  }

  return level;
}

/**
 * ### barHeights
 *
 * The utterance drawn as bars. The envelope above gives each syllable its
 * loudness; the term multiplied into it is the fine ripple a voice leaves
 * between one sample and the next, which is what separates a waveform from
 * a row of hills. Its cycles divide the pattern evenly, so the ripple has
 * no seam either.
 *
 * Rounded to two places before it reaches the markup: the same sum can land
 * on neighbouring doubles on the server and in the browser, and React reads
 * that as the tree having changed underneath it.
 *
 * @returns One height per bar, in drawing units
 */
function barHeights(): ReadonlyArray<number> {
  return Array.from({ length: BAR_COUNT }, (_, index) => {
    const position = index / BAR_COUNT;
    const ripple =
      0.42 +
      0.44 * Math.abs(Math.sin((2 * Math.PI * 43 * index) / BAR_COUNT)) +
      0.14 * Math.abs(Math.sin((2 * Math.PI * 17 * index) / BAR_COUNT + 0.9));

    const height = Math.max(1.4, PEAK * amplitude(position) * ripple);

    return Math.round(height * 100) / 100;
  });
}

const HEIGHTS = barHeights();

/**
 * ### AboutSoundWave
 *
 * The word "디플리", spoken, running left to right past a window that fades
 * at both ends. It is the claim beside it made visible: a written line holds
 * only so much, and even the company's own name carries more as a sound than
 * as four letters.
 *
 * The pattern is drawn twice and slid by exactly one pattern's width, so
 * what leaves one edge is already entering the other and the loop never
 * shows a join. It stands still under a reduced-motion preference, where the
 * drawn shape says the same thing without moving. Decorative, so it is
 * hidden from assistive technology.
 *
 * @returns The rendered waveform element
 */
export default function AboutSoundWave() {
  return (
    <div
      className="w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_16%,black_84%,transparent)]"
      aria-hidden="true"
    >
      <svg
        viewBox={`0 0 ${PATTERN_WIDTH} ${PEAK * 2}`}
        className="h-[52px] w-full lg:h-[72px]"
        preserveAspectRatio="none"
        focusable="false"
      >
        <g
          className="animate-sound-flow motion-reduce:animate-none"
          style={
            { '--sound-flow': `-${PATTERN_WIDTH}px` } as React.CSSProperties
          }
        >
          {[0, 1].map((copy) =>
            HEIGHTS.map((height, index) => (
              <rect
                key={`${copy}-${index}`}
                x={copy * PATTERN_WIDTH + index * BAR_PITCH + 3}
                y={Math.round((PEAK - height) * 100) / 100}
                width={4}
                height={Math.round(height * 200) / 100}
                rx={2}
                fill="currentColor"
              />
            )),
          )}
        </g>
      </svg>
    </div>
  );
}
